/* The animated diagram.
 *
 * A scene is a flat list of primitives plus a list of steps; each step names
 * what is on, what is accented and what is dimmed. The artifact swapped those
 * classes by hand on every tick. Here the step index is state and the classes
 * fall out of it, which is also why the diagram can be scrubbed.
 *
 * It plays itself once, when it comes into view — unless the learner asked for
 * reduced motion, in which case it waits to be played.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FILMS } from '../../data/films';
import { filmClass, itemsById, isPlaced, sparkGeometry, stepRoles, textLines, wirePath, wrapText } from '../../engine/films';
import { useReducedMotion } from '../../hooks/useMedia';
import type { Block, FilmItemSpec, FilmScene } from '../../types';

const AUTOSTART_DELAY = 2200;
const MANUAL_DELAY = 1200;
const STEP_DELAY = 3200;

export function FilmBlock({ block }: { block: Block }) {
  const { t } = useTranslation();
  const scene = block.film ? FILMS[block.film] : undefined;
  const reduce = useReducedMotion();

  const [step, setStep] = useState(0);
  const [playing, setPlaying] = useState(false);
  const firstDelay = useRef(AUTOSTART_DELAY);
  const autostarted = useRef(false);
  const trackRef = useRef<HTMLDivElement>(null);

  const total = scene?.steps.length ?? 0;
  const last = total - 1;

  /* Watch it once before you answer: play on arrival, once, unless motion is
     reduced. Deferred to the next frame rather than set here, so mounting an
     effect is not itself a render. */
  useEffect(() => {
    if (reduce || autostarted.current) return;
    autostarted.current = true;
    requestAnimationFrame(() => setPlaying(true));
  }, [reduce]);

  useEffect(() => {
    if (!playing || !scene || step >= last) return;
    const delay = step === 0 ? firstDelay.current : STEP_DELAY;
    const timer = setTimeout(() => {
      const next = Math.min(step + 1, last);
      setStep(next);
      if (next >= last) setPlaying(false);
    }, delay);
    return () => clearTimeout(timer);
  }, [playing, step, last, scene]);

  const roles = useMemo(() => stepRoles(scene?.steps[step] ?? { on: [] }), [scene, step]);

  if (!scene || !block.film) return null;

  const current = scene.steps[step];

  const togglePlay = () => {
    if (playing) {
      setPlaying(false);
      return;
    }
    firstDelay.current = MANUAL_DELAY;
    if (step >= last) setStep(0);
    setPlaying(true);
  };

  const scrubTo = (clientX: number) => {
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect) return;
    const fraction = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    setPlaying(false);
    setStep(Math.round(fraction * last));
  };

  return (
    <figure className="fig" data-od-id={`film-${block.id}`} data-filmid={block.id}>
      <div className="fig-head">
        <span className="gauge">
          {total} {t('stepsW')}
        </span>
        <span className="rule" />
      </div>
      <div className="fig-body">
        <div className="film">
          <div className="film-stage" id={`fstage-${block.id}`}>
            <FilmSceneSvg scene={scene} roles={roles} />
          </div>

          <div className="film-bar">
            <span className="fc num" id={`fcount-${block.id}`}>
              {step + 1} / {total}
            </span>

            <div className="fctl">
              <button
                type="button"
                data-act="film-prev"
                data-id={block.id}
                aria-label={t('prevStep')}
                onClick={() => {
                  setPlaying(false);
                  setStep((s) => Math.max(0, s - 1));
                }}
              >
                <svg className="st" viewBox="0 0 20 20" aria-hidden="true">
                  <path d="M12 5l-4 5 4 5" />
                </svg>
              </button>
              <button
                type="button"
                data-act="film-play"
                data-id={block.id}
                id={`fplay-${block.id}`}
                aria-label={playing ? t('pause') : t('play')}
                onClick={togglePlay}
              >
                {playing ? (
                  <svg viewBox="0 0 20 20" aria-hidden="true">
                    <rect x="5.5" y="4.5" width="3.2" height="11" rx="1" />
                    <rect x="11.3" y="4.5" width="3.2" height="11" rx="1" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 20 20" aria-hidden="true">
                    <path d="M6.5 4.5l9 5.5-9 5.5z" />
                  </svg>
                )}
              </button>
              <button
                type="button"
                data-act="film-next"
                data-id={block.id}
                aria-label={t('nextStep')}
                onClick={() => {
                  setPlaying(false);
                  setStep((s) => Math.min(last, s + 1));
                }}
              >
                <svg className="st" viewBox="0 0 20 20" aria-hidden="true">
                  <path d="M8 5l4 5-4 5" />
                </svg>
              </button>
            </div>

            <div
              className="track"
              data-act="film-scrub"
              data-id={block.id}
              id={`ftrack-${block.id}`}
              role="slider"
              tabIndex={0}
              aria-label={t('watchTitle')}
              aria-valuemin={1}
              aria-valuemax={total}
              aria-valuenow={step + 1}
              ref={trackRef}
              onClick={(event) => scrubTo(event.clientX)}
              onKeyDown={(event) => {
                const keys: Record<string, number> = {
                  ArrowRight: Math.min(last, step + 1),
                  ArrowUp: Math.min(last, step + 1),
                  ArrowLeft: Math.max(0, step - 1),
                  ArrowDown: Math.max(0, step - 1),
                  Home: 0,
                  End: last,
                };
                if (!(event.key in keys)) return;
                event.preventDefault();
                setPlaying(false);
                setStep(keys[event.key]);
              }}
            >
              <i id={`ffill-${block.id}`} style={{ width: `${((step + 1) / total) * 100}%` }} />
            </div>

            <div className="dots" id={`fdots-${block.id}`}>
              {scene.steps.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  data-act="film-go"
                  data-id={block.id}
                  data-i={i}
                  aria-current={i === step}
                  aria-label={`${t('step')} ${i + 1}`}
                  onClick={() => {
                    setPlaying(false);
                    setStep(i);
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
      <figcaption className="fig-foot">
        <span className="fig-cap" id={`fcap-${block.id}`} aria-live="polite">
          {t(current.capKey)}
        </span>
      </figcaption>
    </figure>
  );
}

/* ── the scene itself ─────────────────────────────────────────────── */
function FilmSceneSvg({ scene, roles }: { scene: FilmScene; roles: ReturnType<typeof stepRoles> }) {
  const { t } = useTranslation('films');
  const items = itemsById(scene);
  const wires = scene.items.filter((item) => item.k === 'wire');
  const shapes = scene.items.filter((item) => item.k !== 'wire');

  return (
    <svg viewBox={`0 0 ${scene.w} ${scene.h}`} role="img">
      <g style={{ color: 'var(--accent-strong)' }}>
        {wires.map((wire) => {
          if (wire.k !== 'wire') return null;
          const from = items[wire.from];
          const to = items[wire.to];
          if (!isPlaced(from) || !isPlaced(to)) return null;
          return (
            <g key={wire.id} className={filmClass(wire.id, roles)} data-f={wire.id}>
              <path className={wire.dash ? 'fwire dash' : 'fwire'} d={wirePath(from, to)} />
            </g>
          );
        })}

        {shapes.map((item) => (
          <FilmShape key={item.id} item={item} roles={roles} label={t} />
        ))}
      </g>
    </svg>
  );
}

function FilmShape({
  item,
  roles,
  label,
}: {
  item: FilmItemSpec;
  roles: ReturnType<typeof stepRoles>;
  label: (key: string) => string;
}) {
  const className = filmClass(item.id, roles);

  if (item.k === 'frame') {
    return (
      <g className={className} data-f={item.id}>
        <rect className="fframe" x={item.x} y={item.y} width={item.w} height={item.h} rx={10} />
        <text className="fcap" x={item.x + 12} y={item.y + 18}>
          {wrapText(label(item.lKey), item.w - 24, 9)[0]}
        </text>
      </g>
    );
  }

  if (item.k === 'box' || item.k === 'chip') {
    const cx = item.x + item.w / 2;
    const size = item.k === 'chip' ? 11.5 : 12.5;
    const lines = wrapText(label(item.lKey), item.w - 18, size);
    const sub = item.sKey ? wrapText(label(item.sKey), item.w - 18, 11) : null;
    const cy = item.y + item.h / 2 - (sub ? 6 : 0);
    return (
      <g className={className} data-f={item.id}>
        <rect className="fbox" x={item.x} y={item.y} width={item.w} height={item.h} rx={item.k === 'chip' ? 18 : 8} />
        <TextLines lines={textLines(lines, cx, cy, size)} className="flbl" />
        {sub && <TextLines lines={textLines(sub, cx, item.y + item.h - 13, 11)} className="fsub" />}
      </g>
    );
  }

  if (item.k === 'bar') {
    return (
      <g className={className} data-f={item.id}>
        <rect className="fbar-bg" x={item.x} y={item.y} width={item.w} height={item.h} rx={item.h / 2} />
        <rect className="fbar" x={item.x} y={item.y} width={Math.max(3, item.w * item.v)} height={item.h} rx={item.h / 2} />
        <text className="fcap" x={item.x} y={item.y + item.h + 11}>
          {wrapText(label(item.lKey), item.w + 40, 9)[0]}
        </text>
      </g>
    );
  }

  if (item.k === 'spark') {
    const { points, area } = sparkGeometry(item);
    return (
      <g className={className} data-f={item.id}>
        <path className="fbar-bg" style={{ opacity: 0.45 }} d={area} />
        <polyline points={points} fill="none" stroke="currentColor" strokeWidth={2} strokeLinejoin="round" />
      </g>
    );
  }

  if (item.k === 'note') {
    return (
      <g className={className} data-f={item.id}>
        <text className="fsub" style={{ fontSize: 11 }}>
          {textLines(wrapText(label(item.lKey), 240, 11), item.x, item.y + 11, 11).map((line, i) => (
            <tspan key={i} x={line.x} y={line.y}>
              {line.text}
            </tspan>
          ))}
        </text>
      </g>
    );
  }

  return null;
}

function TextLines({ lines, className }: { lines: { x: number; y: number; text: string }[]; className: string }) {
  return (
    <text className={className} textAnchor="middle">
      {lines.map((line, i) => (
        <tspan key={i} x={line.x} y={line.y}>
          {line.text}
        </tspan>
      ))}
    </text>
  );
}
